// Shared shapes that the api returns. Keep in sync with apps/api/src/routes/*.

export type Role = "ADMIN" | "MEMBER";

export type User = {
  id: string;
  email: string;
  name: string | null;
  colour: string;
  ntfyTopic: string | null;
  venmoHandle: string | null;
  zelleHandle: string | null;
  createdAt: string;
};

export type UserWithFlats = User & {
  flats: Array<{ id: string; name: string; role: Role }>;
};

export type FlatMember = {
  userId: string;
  role: Role;
  joinedAt: string;
  user: Pick<User, "id" | "email" | "name" | "colour">;
};

export type Flat = {
  id: string;
  name: string;
  inviteCode: string;
  createdAt: string;
  members: FlatMember[];
};

export type ApplianceName = "WASHER" | "DRYER" | "DISHWASHER";

export const APPLIANCE_LABEL: Record<ApplianceName, string> = {
  WASHER: "Washer",
  DRYER: "Dryer",
  DISHWASHER: "Dishwasher",
};
export type ApplianceState = "RUNNING" | "DONE" | "IDLE";
export type EventSource = "MANUAL" | "NFC" | "SENSOR" | "HA";

export type Appliance = {
  id: string;
  flatId: string;
  name: ApplianceName;
  state: ApplianceState;
  nextApplianceId: string | null;
  haManaged: boolean;
  claim?: { userId: string; at: string | null; user: { name: string | null; colour: string } | null } | null;
  runOwner?: { name: string | null; colour: string } | null;
  lastEvent?: { state: ApplianceState; timestamp: string; userId: string | null; user: { name: string | null; colour: string } | null } | null;
};

export type BookingStatus = "PENDING" | "ACTIVE" | "DONE" | "CANCELLED";

export type Booking = {
  id: string;
  applianceId: string;
  userId: string;
  slotStart: string;
  slotEnd: string;
  status: BookingStatus;
  user: Pick<User, "id" | "name" | "colour">;
};

export type ChoreSource = "MANUAL" | "AUTO";
export type ChoreStatus = "PENDING" | "DONE" | "ESCALATED";

export type Chore = {
  id: string;
  flatId: string;
  name: string;
  assigneeId: string | null;
  creatorId: string | null;
  source: ChoreSource;
  status: ChoreStatus;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
  assignee: Pick<User, "id" | "name" | "colour"> | null;
};

export type ExpenseSplit = {
  id: string;
  userId: string;
  amount: number;
  settled: boolean;
  user: Pick<User, "id" | "name" | "colour">;
};

export type Expense = {
  id: string;
  flatId: string;
  paidById: string;
  amount: number;
  description: string;
  createdAt: string;
  paidBy: Pick<User, "id" | "name" | "colour">;
  splits: ExpenseSplit[];
};

export type SettlementEntry = {
  userId: string;
  user: Pick<User, "id" | "name" | "colour">;
  net: number; // positive = owed money, negative = owes money
};

// A direct debt: `from` owes `to` `amount` (pence). The payee carries payment
// handles so the UI can deep-link to Venmo / hand off to Zelle.
export type Debt = {
  fromUserId: string;
  toUserId: string;
  amount: number;
  from: Pick<User, "id" | "name" | "colour">;
  to: Pick<User, "id" | "name" | "colour" | "venmoHandle" | "zelleHandle">;
};

export type ShoppingItem = {
  id: string;
  flatId: string;
  name: string;
  addedById: string;
  got: boolean;
  createdAt: string;
  addedBy: Pick<User, "id" | "name" | "colour">;
};

export type ItemCategory = "KITCHEN" | "LAUNDRY" | "BATHROOM" | "LIVING" | "CLEANING" | "OTHER";

export const ITEM_CATEGORIES: ItemCategory[] = [
  "KITCHEN",
  "LAUNDRY",
  "BATHROOM",
  "LIVING",
  "CLEANING",
  "OTHER",
];

export const ITEM_CATEGORY_LABEL: Record<ItemCategory, string> = {
  KITCHEN: "Kitchen",
  LAUNDRY: "Laundry",
  BATHROOM: "Bathroom",
  LIVING: "Living",
  CLEANING: "Cleaning",
  OTHER: "Other",
};

export type Item = {
  id: string;
  flatId: string;
  name: string;
  category: ItemCategory;
  bringerId: string | null;
  note: string | null;
  photoKey: string | null;
  photoUpdatedAt: string | null;
  createdAt: string;
  bringer: Pick<User, "id" | "name" | "colour"> | null;
  photoUrl: string | null; // presigned GET, generated server-side
};

export type NoticeTag = "URGENT" | "SOCIAL" | "RULES" | "GENERAL";

export type Notice = {
  id: string;
  flatId: string;
  authorId: string;
  title: string;
  body: string;
  tag: NoticeTag;
  createdAt: string;
  author: Pick<User, "id" | "name" | "colour">;
};

export type AuthResponse = {
  accessToken: string;
  user: User;
};
